/**
 * TopBar Component
 *
 * Dark top navigation bar that contains the universal search functionality.
 * Designed to match the sidebar aesthetic with consistent dark theme styling.
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  FiSearch, FiCommand, FiPlus, FiUser, FiMenu, FiX, FiBriefcase
} from 'react-icons/fi';
import { useNavigate } from 'react-router-dom';
import { useSearch } from '../context/SearchContext';
import { useAuth } from '../context/AuthContext';
import { useOrganization } from '../context/OrganizationContext';
import QuickCreateModal from './QuickCreateModal';
import TopBarSearchResults from './TopBarSearchResults';
import NotificationCenter from './NotificationCenter';
import DarkModeToggle from './DarkModeToggle';
import '../styles/TopBar.css';

const TopBar = ({ onMobileMenuToggle }) => {
  const navigate = useNavigate();
  const { user, getEffectiveRole } = useAuth();
  const { organizationConfig, currentOrganization } = useOrganization();
  const {
    searchQuery,
    searchResults,
    isLoading,
    recentSearches,
    searchType,
    setSearchQuery,
    setSearchType,
    handleResultSelect
  } = useSearch();

  const [isQuickCreateOpen, setIsQuickCreateOpen] = useState(false);
  const [isSearchFocused, setIsSearchFocused] = useState(false);
  const [effectiveRole, setEffectiveRole] = useState(null);
  const searchRef = useRef(null);
  const searchInputRef = useRef(null);

  // Fetch effective role for display
  useEffect(() => {
    const fetchRole = async () => {
      if (user && getEffectiveRole) {
        const role = await getEffectiveRole();
        setEffectiveRole(role);
      }
    };
    fetchRole();
  }, [user, getEffectiveRole]);

  // Listen for role emulation changes
  useEffect(() => {
    const handleStorageChange = (e) => {
      if (e.key === 'emulated_role_id') {
        const fetchRole = async () => {
          if (user && getEffectiveRole) {
            const role = await getEffectiveRole();
            setEffectiveRole(role);
          }
        };
        fetchRole();
      }
    };

    window.addEventListener('storage', handleStorageChange);
    return () => window.removeEventListener('storage', handleStorageChange);
  }, [user, getEffectiveRole]);

  const handleSearchFocus = () => {
    setIsSearchFocused(true);
  };

  const handleSearchBlur = () => {
    // Delay to allow click events on results to fire
    setTimeout(() => {
      setIsSearchFocused(false);
      // Reset search type to 'all' when closing search
      setSearchType('all');
    }, 200);
  };

  const handleResultClick = (result) => {
    handleResultSelect(result);
    // Don't navigate for activity types - they're just informational
    if (result.type !== 'activity') {
      navigate(result.url);
    }
    setSearchQuery('');
    setIsSearchFocused(false);
  };

  // Close search results when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (searchRef.current && !searchRef.current.contains(event.target)) {
        setIsSearchFocused(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Keyboard shortcut (Cmd+K or Ctrl+K) to focus search, Escape to close
  useEffect(() => {
    const handleKeyDown = (event) => {
      if ((event.metaKey || event.ctrlKey) && event.key === 'k') {
        event.preventDefault();

        // Get any highlighted/selected text
        const selectedText = window.getSelection().toString().trim();

        // If there's selected text, use it as the search query
        if (selectedText) {
          setSearchQuery(selectedText);
        }

        // Focus the search input and show results
        searchInputRef.current?.focus();
        setIsSearchFocused(true);
      }

      // Escape key to close search
      if (event.key === 'Escape' && isSearchFocused) {
        event.preventDefault();
        setIsSearchFocused(false);
        setSearchQuery('');
        setSearchType('all');
        searchInputRef.current?.blur();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [setSearchQuery, isSearchFocused, setSearchType]);

  const handleQuickCreateOpen = () => {
    setIsQuickCreateOpen(true);
  };

  const handleQuickCreateClose = () => {
    setIsQuickCreateOpen(false);
  };

  const handleCloseSearch = () => {
    setIsSearchFocused(false);
    setSearchQuery('');
    setSearchType('all');
    searchInputRef.current?.blur();
  };

  // Platform-specific keyboard shortcut display
  const isMac = navigator.platform.toUpperCase().indexOf('MAC') >= 0;
  const shortcutKey = isMac ? '⌘' : 'Ctrl';

  // Get user's display name (first name + last initial or full name)
  const getUserDisplayName = () => {
    if (!user) return '';
    if (user.first_name && user.last_name) {
      return `${user.first_name} ${user.last_name.charAt(0)}.`;
    }
    if (user.first_name) return user.first_name;
    if (user.username) return user.username;
    return 'User';
  };

  // Get user's role display name (use effectiveRole to show emulated role)
  const getUserRole = () => {
    if (effectiveRole) {
      return effectiveRole.name || '';
    }
    if (!user || !user.full_role) return '';
    return user.full_role.name || user.role_name || '';
  };

  return (
    <>
      <div className="top-bar inv-no-print">
        {/* Mobile Menu Toggle - CSS handles visibility based on screen size */}
        {onMobileMenuToggle && (
          <button
            className="topbar-mobile-menu-toggle"
            onClick={onMobileMenuToggle}
            aria-label="Open Menu"
            title="Open Menu"
          >
            <FiMenu size={20} />
          </button>
        )}

        {/* Left Section - Dark Mode Toggle and Organization Badge */}
        <div className="top-bar-left">
          <DarkModeToggle />
          {currentOrganization && (
            <div className="topbar-org-badge" title={organizationConfig?.name || currentOrganization}>
              <FiBriefcase size={14} />
              <span className="topbar-org-badge-name">
                {organizationConfig?.name || currentOrganization}
              </span>
            </div>
          )}
        </div>

        <div className="top-bar-content" ref={searchRef}>
          {/* Universal Search Input */}
          <div className="top-bar-search-container">
            <div className="top-bar-search-input-wrapper">
              <FiSearch className="topbar-search-icon" />
              <input
                ref={searchInputRef}
                type="text"
                className="top-bar-search-input"
                placeholder="Search anything..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                onFocus={handleSearchFocus}
                onBlur={handleSearchBlur}
                title="Search (Cmd+K)"
              />
              {isSearchFocused && (
                <button
                  className="topbar-search-close"
                  onClick={handleCloseSearch}
                  onMouseDown={(e) => e.preventDefault()}
                  aria-label="Close search"
                >
                  <FiX size={16} />
                </button>
              )}
              <div className="topbar-search-shortcut">
                {isMac ? <FiCommand size={12} /> : <span>{shortcutKey}</span>}
                <span>K</span>
              </div>
            </div>

            {/* Search Results Dropdown */}
            <TopBarSearchResults
              searchQuery={searchQuery}
              searchResults={searchResults}
              isLoading={isLoading}
              recentSearches={recentSearches}
              searchType={searchType}
              onSearchTypeChange={setSearchType}
              onResultClick={handleResultClick}
              isOpen={isSearchFocused}
            />
          </div>

          {/* Quick Create Button */}
          <button
            className="top-bar-quick-create"
            onClick={handleQuickCreateOpen}
            title="Quick create"
          >
            <FiPlus size={20} />
          </button>
        </div>

        {/* User Badge */}
        {user && (
          <div className="top-bar-right">
            {/* Notification Center */}
            <NotificationCenter />

            <div
              className="topbar-user-badge"
              onClick={() => navigate('/account')}
              role="button"
              tabIndex={0}
              title="View profile"
              style={{ cursor: 'pointer' }}
            >
              <div className="topbar-user-badge-icon">
                <FiUser size={16} />
              </div>
              <div className="topbar-user-badge-info">
                <span className="topbar-user-badge-name">{getUserDisplayName()}</span>
                {getUserRole() && (
                  <span className="topbar-user-badge-role">{getUserRole()}</span>
                )}
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Quick Create Modal */}
      <QuickCreateModal
        isOpen={isQuickCreateOpen}
        onClose={handleQuickCreateClose}
      />
    </>
  );
};

export default TopBar;
